import { useState } from "react";
import { Button, Card, LoadingAnimation, Stack, Table, type TableColumn } from "@knowtex-ai/components";
import { Download } from "iconoir-react";
import type { McArtifact } from "../../lib/mc";
import { useMcArtifactUrlQuery } from "../../hooks/useMcRuns";
import { fileName, useOpenArtifact } from "./ArtifactDownloadButton";
import { GAP, SectionHeading } from "./layout";
import RowActions from "./RowActions";
import { formatBytes, formatCount, groupArtifacts, type ArtifactKind } from "./format";

const INITIAL_THUMBNAILS = 24;

function Thumbnail({ runId, artifact }: { runId: string; artifact: McArtifact }) {
  const { data: url, isError } = useMcArtifactUrlQuery(runId, artifact.key);
  return (
    <a
      href={url}
      target="_blank"
      rel="noreferrer"
      title={artifact.key}
      className="group block overflow-hidden rounded-md border border-gray-200 bg-gray-50"
    >
      <div className="aspect-video flex items-center justify-center">
        {url ? (
          <img src={url} alt={artifact.key} loading="lazy" className="h-full w-full object-cover" />
        ) : isError ? (
          <span className="text-xs text-gray-400">Unavailable</span>
        ) : (
          <LoadingAnimation size={18} />
        )}
      </div>
      <div className="truncate px-2 py-1 text-[11px] font-mono text-gray-600 group-hover:underline">
        {artifact.missionId ? `${artifact.missionId}/` : ""}
        {fileName(artifact.key)}
      </div>
    </a>
  );
}

function MediaArtifact({ runId, artifact, kind }: { runId: string; artifact: McArtifact; kind: ArtifactKind }) {
  const [playing, setPlaying] = useState(false);
  const { data: url, error } = useMcArtifactUrlQuery(runId, artifact.key, { enabled: playing });
  const openArtifact = useOpenArtifact(runId);
  return (
    <li className="py-3">
      <div className="flex items-center gap-3 text-sm">
        <span className="font-mono text-gray-800 break-all flex-1">{artifact.key}</span>
        <span className="text-gray-500 shrink-0">{formatBytes(artifact.bytes)}</span>
        {!playing && (
          <Button size="small" variant="secondary" onClick={() => setPlaying(true)}>
            Play
          </Button>
        )}
        <Button size="small" variant="text" onClick={() => openArtifact(artifact.key)}>
          Download
        </Button>
      </div>
      {playing &&
        (error ? (
          <p className="mt-3 text-sm text-red-600">{error.message}</p>
        ) : !url ? (
          <div className="mt-3">
            <LoadingAnimation size={18} />
          </div>
        ) : kind === "video" ? (
          <video src={url} controls className="mt-3 max-h-[480px] w-full rounded-md bg-black" />
        ) : (
          <audio src={url} controls className="mt-3 w-full" />
        ))}
    </li>
  );
}

function FileArtifacts({ runId, files }: { runId: string; files: McArtifact[] }) {
  const openArtifact = useOpenArtifact(runId);
  const columns: TableColumn<McArtifact>[] = [
    {
      id: "key",
      header: "File",
      width: 560,
      grow: true,
      accessor: "key",
      sortable: true,
      cell: (a) => <span className="font-mono break-all">{a.key}</span>,
    },
    { id: "mission", header: "Mission", width: 120, accessor: "missionId", sortable: true },
    { id: "mime", header: "Type", width: 150, accessor: "mime", sortable: true },
    { id: "bytes", header: "Size", width: 100, align: "end", accessor: "bytes", sortable: true, cell: (a) => formatBytes(a.bytes) },
    {
      id: "actions",
      header: <span className="sr-only">Actions</span>,
      width: 64,
      sticky: "end",
      hideable: false,
      cell: (a) => (
        <RowActions
          label={`Actions for ${fileName(a.key)}`}
          actions={[{ key: "download", label: `Download ${fileName(a.key)}`, icon: Download, onClick: () => openArtifact(a.key) }]}
        />
      ),
    },
  ];
  return (
    <Table
      aria-label="Other artifacts"
      size="small"
      columns={columns}
      data={files}
      getRowId={(a) => a.key}
      paginated={false}
      maxHeight={480}
    />
  );
}

export default function RunArtifacts({ runId, artifacts }: { runId: string; artifacts: McArtifact[] }) {
  const [showAllImages, setShowAllImages] = useState(false);
  const groups = groupArtifacts(artifacts);
  const images = showAllImages ? groups.image : groups.image.slice(0, INITIAL_THUMBNAILS);
  const media: [ArtifactKind, McArtifact][] = [
    ...groups.video.map((a): [ArtifactKind, McArtifact] => ["video", a]),
    ...groups.audio.map((a): [ArtifactKind, McArtifact] => ["audio", a]),
  ];

  return (
    <Card variant="elevated" title="Artifacts" subtitle={`${formatCount(artifacts.length)} files`}>
      {artifacts.length === 0 ? (
        <p className="text-sm text-gray-500">No artifacts uploaded yet.</p>
      ) : (
        <Stack gap={GAP.section}>
          {groups.image.length > 0 && (
            <section>
              <SectionHeading>Screenshots ({formatCount(groups.image.length)})</SectionHeading>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
                {images.map((artifact) => (
                  <Thumbnail key={artifact.key} runId={runId} artifact={artifact} />
                ))}
              </div>
              {groups.image.length > INITIAL_THUMBNAILS && !showAllImages && (
                <div className="mt-4">
                  <Button size="small" variant="secondary" onClick={() => setShowAllImages(true)}>
                    Show all {formatCount(groups.image.length)} screenshots
                  </Button>
                </div>
              )}
            </section>
          )}
          {media.length > 0 && (
            <section>
              <SectionHeading className="!mb-1">Video and audio</SectionHeading>
              <ul className="divide-y divide-gray-100">
                {media.map(([kind, artifact]) => (
                  <MediaArtifact key={artifact.key} runId={runId} artifact={artifact} kind={kind} />
                ))}
              </ul>
            </section>
          )}
          {groups.file.length > 0 && (
            <section>
              <SectionHeading>Other files ({formatCount(groups.file.length)})</SectionHeading>
              <FileArtifacts runId={runId} files={groups.file} />
            </section>
          )}
        </Stack>
      )}
    </Card>
  );
}
